import { SuperGif }                        from '@wizpanda/super-gif';
import { EventBus,
         EmotionTypeEnum,
         EmotionIdFromString,
         EmotionTypeFromString,
         ConfigService }                   from '../../..';
import   axios,
       { AxiosInstance }                   from 'axios';


/**
 * The service that implements the functions related to avatar emotions
 * flow and state. 
 */
export class EmotionsService {

      /** Animated Avatar objects */
      public static animatedAvatars: Map<string, SuperGif> = new Map<string, SuperGif>();
      
      /** Emotion Avatar URLs */
      public static bigAvatarUrls: Map<string, string> = new Map<string, string>();
      public static smallAvatarUrls: Map<string, string> = new Map<string, string>();

      /**
       * TODO : Retrieve avatars and handle 404 error status
       */
      public static async retrieveAvatars(avatarUrl: string, avatarFileFormat: string) {
         const httpConnector: AxiosInstance = axios.create({   baseURL: avatarUrl,
                                                               timeout: ConfigService.getMediaRequestTimeout(),
                                                               withCredentials: false
                                                            });
               
         await httpConnector.get(avatarUrl, { headers: { 'Content-Type': `image/${avatarFileFormat}`} })
                            .then((response: any) => {
                            })
                            .catch((err: any) => {
                            });
      }

      /**
       * Initialize the lists of vatars URLs, each emotion avatar URL is verified, only the
       * configured avatars are set into the lists.
       * 
       * @param isBigSize - Indicates whether the big size avatar is concerned or not.
       * @param isAnimated - Indicates whether the animated avatar is concerned or not.
       * @param loadedAvatarsCounter - The counter of loaded avatar.
       */
      public static initEmotionAvatarUrls(isBigSize: boolean, isAnimated: boolean, loadedAvatarsCounter: number): number {
         const avatarResourceUri: string = ConfigService.getBotResourceURI();
         for (const emotion in EmotionTypeEnum) {
            const emoValue: string = EmotionTypeFromString[emotion];

            let avatarUrl: string;
            if (isBigSize) {
               avatarUrl = isAnimated ? `${avatarResourceUri}_big_${emoValue}.gif`
                                      : `${avatarResourceUri}_big_${emoValue}.png`;
            } else {
               avatarUrl = isAnimated ? `${avatarResourceUri}_${emoValue}.gif`
                                      : `${avatarResourceUri}_${emoValue}.png`;
            }
            (isBigSize) ? EmotionsService.bigAvatarUrls.set(emoValue, avatarUrl)
                        : EmotionsService.smallAvatarUrls.set(emoValue, avatarUrl);
            loadedAvatarsCounter++;  
         }
         return loadedAvatarsCounter;
      }

      /**
       * Verify valid avatar URLs.
       */
      public static verifyValidAvatarUrls(bootstrapCallback?: Function) {
         let counter: number = 0;
         const total: number = Object.keys(EmotionTypeEnum).length * 2;

         if (EmotionsService.areAnimatedEmotionAvatarsConfigured()) {
            counter = EmotionsService.initEmotionAvatarUrls(true, true, counter); // init big and animated avatars
            counter = EmotionsService.initEmotionAvatarUrls(false, true, counter); // init small and animated avatars
         } else {
            counter = EmotionsService.initEmotionAvatarUrls(true, false, counter); // init big and static avatars
            counter = EmotionsService.initEmotionAvatarUrls(false, false, counter); // init small and static avatars
         }

         if (counter === total && bootstrapCallback) {
            bootstrapCallback();
         }
      }

      /**
       * Initialize the GIF state controler entity.
       * 
       * @param emotion - The emotion associated to the animated avatar entity
       * @param imgElem - The image HTML element configured with the static and the animated avatar
       * @param maxWidth - The maximal width to set into animation canvas
       */
      public static initAnimatedAvatars(emotion: string, imgElem: any, maxWidth: number) {
         const isInfinite: boolean = ( emotion === EmotionTypeEnum.PRESENTATION || 
                                       emotion === EmotionTypeEnum.NEUTRAL || 
                                       emotion === EmotionTypeEnum.THINK);

         const gifObj: SuperGif = new SuperGif( imgElem,
                                            { loop_mode: isInfinite,
                                              auto_play: isInfinite,
                                              maxWidth: maxWidth, // allow adapted scaling in GIF canvas when container is smaller than the GIF width size
                                              progressbar_height: 0,
                                              onEnd: (elem: any) => { 
                                                const emotionId: string = EmotionIdFromString[elem.id];
                                                if (emotionId !== EmotionIdFromString[EmotionTypeEnum.PRESENTATION] &&
                                                    emotionId !== EmotionIdFromString[EmotionTypeEnum.NEUTRAL] &&
                                                    emotionId !== EmotionIdFromString[EmotionTypeEnum.THINK]) {
                                                   EventBus.$emit('ON_ANIMATION_LOOP_ENDED_EVENT', elem.id); 
                                                }
                                              }
                                            });
         gifObj.load(() => {
            EmotionsService.animatedAvatars.set(EmotionIdFromString[emotion], gifObj);
         });
      }

      /**
       * Play animated emotion associated to the given emotion.
       * 
       * @param emotionId - The emotion identifier to play
       */
      public static playEmotionAnimation(emotionId: string) {
         const gifObj: SuperGif | undefined = EmotionsService.animatedAvatars.get(emotionId);
         if (gifObj) {
            gifObj.moveTo(0); // restart animation from the begining
            gifObj.play(); // play animation
         }
      }

      /**
       * Stop animated emotion associated to the given emotion.
       * 
       * @param emotionId - The emotion identifier to stop
       */
      public static stopEmotionAnimation(emotionId: string) {
         const gifObj: SuperGif | undefined = EmotionsService.animatedAvatars.get(emotionId);
         if (gifObj) {
            gifObj.pause(); // stop animation
         }
      }

      /**
       * Return whether the emotions feature is available giving the emoAvatarFormat parameter, when the value is empty string 
       * then the feature is no configured, when the png for static or gif for animated avatars then the feature is activated.
       * 
       * @return TRUE whether emotion feature is available. FALSE otherwise.
       */
      public static isEmotionFeatureAvailable(): boolean {
         return (ConfigService.getEmotionAvatarFormat() !== null);
      }

      /**
       * Indicates wheter emotion avatars are available in any of the possible sizes : big or small in PNG file format.
       * 
       * @return TRUE whether at least one static emotion avatar is configured. FALSE otherwise.
       */
      public static areStaticEmotionAvatarConfigured(): boolean {
         return  ConfigService.getEmotionAvatarFormat() !== null && ConfigService.getEmotionAvatarFormat() !== 'gif';
      }

      /**
       * Indicates wheter emotion avatars are available in any of the possible sizes : big or small in GIF file format.
       * 
       * @return TRUE whether at least one animated emotion avatar is configured. FALSE otherwise.
       */
      public static areAnimatedEmotionAvatarsConfigured(): boolean {
         return ConfigService.getEmotionAvatarFormat() === 'gif';
      }
}
